import {
  BackrsFormField,
  BackrsFormValues,
  CareerCategory,
  ProfileOptionAnswer,
} from '@backrs_ui/common-ui';

import { OnboardingQuestion } from '../onboarding.types';

/**
 * These keys are used to transform form values into the expected profile option answer shape.
 *
 * Form values meaning this is a profile option answer, but the UI presents it within a FORM mixed in with user data
 */
export const PROFILE_FORM_KEYS = {
  CAREER_PATH_CATEGORY: 'career_path_category',
  CAREER_PATH_SUBCATEGORY: 'career_path_',
  CAREER_PATH_FIELD: 'career_path_field',
  ZIP_CODE: 'zip_code',
  LINKEDIN_URL: 'linkedin_url',
  SCHOOL: 'school',
} as const;

type HandleBuildProfileOptionAnswersProps = Readonly<{
  formData: BackrsFormValues;
  userId: number;
  questions: Record<string, OnboardingQuestion> | undefined;
  careerPathCategories: CareerCategory[];
}>;

/**
 * Orchestrator function that formats the form data into the expected profile option answer shape.
 *
 * [key]: [
 *  {
 *    ...profileOptionAnswer,
 *  },
 *  ...
 * ]
 */

export const getProfileOptionKeysByQuestionId = ({
  formData,
  userId,
  questions,
  careerPathCategories,
}: HandleBuildProfileOptionAnswersProps) => {
  const profileOptionAnswersToPost = handleBuildProfileOptionAnswers({
    formData,
    userId,
    questions,
    careerPathCategories,
  });

  const profileOptionKeysByQuestionId = profileOptionAnswersToPost.reduce(
    (acc, answer) => {
      const questionId =
        answer.category_id !== 'onboarding' ? answer.question_id : answer.category_id;

      if (!acc[questionId]) {
        acc[questionId] = [];
      }

      acc[questionId].push(answer);

      return acc;
    },
    {} as Record<string, ProfileOptionAnswer[]>,
  );

  return profileOptionKeysByQuestionId;
};

const handleBuildProfileOptionAnswers = ({
  formData,
  userId,
  questions,
  careerPathCategories,
}: HandleBuildProfileOptionAnswersProps) => {
  const profileOptionAnswersToPost: ProfileOptionAnswer[] = [];

  // Get the actual question objects from questions for the non-user fields
  const questionsAndResponses: {
    questionId: string;
    question?: OnboardingQuestion;
    answer: string[];
  }[] = Object.keys(formData)
    .map((key) => {
      const question = questions?.[key];

      const answer = Array.isArray(formData[key]) ? formData[key] : [formData[key]];

      const formattedProfileOptionAnswers = formatFormQuestions({
        key,
        answer,
        userId,
        careerPathCategories,
        question,
      });

      // if there are profile option answers embedded in a FORM type, then we will push them to the profile option answers to post
      if (formattedProfileOptionAnswers) {
        profileOptionAnswersToPost.push(...formattedProfileOptionAnswers);
      }

      // otherwise we return the question and answer for checkboxes and radio buttons
      return {
        questionId: key,
        question,
        answer,
      };
    })
    .filter((question) => question !== undefined);

  // otherwise we will handle the checkbox and radio questions
  const handleCheckboxAndRadio = () => {
    questionsAndResponses.forEach((responseAnswer) => {
      const { question } = responseAnswer;

      responseAnswer.answer.forEach((answer) => {
        if (
          question?.question.type === 'FORM' ||
          !question?.question.profile_option_category_id ||
          !question?.question.profile_option_question_id
        ) {
          return;
        }

        const profileOptionAnswer: ProfileOptionAnswer = {
          user_id: userId,
          category_id: question?.question.profile_option_category_id,
          question_id: question?.question.profile_option_question_id,
          option_id: question?.question.profile_option_option_id,
          suboption_id: question?.question.profile_option_suboption_id,
          value: answer,
          source: question?.question.profile_option_source,
        };

        profileOptionAnswersToPost.push(profileOptionAnswer);
      });
    });
  };

  handleCheckboxAndRadio();

  return profileOptionAnswersToPost;
};

type FormatFormQuestionsProps = Readonly<{
  key: string;
  answer: string[];
  userId: number;
  careerPathCategories: CareerCategory[];
  question?: OnboardingQuestion;
}>;

const formatFormQuestions = ({
  key,
  answer,
  userId,
  careerPathCategories,
  question,
}: FormatFormQuestionsProps): ProfileOptionAnswer[] | undefined => {
  if (key === PROFILE_FORM_KEYS.CAREER_PATH_CATEGORY) {
    if (answer?.length === 0 || answer[0] === undefined) {
      return undefined;
    }

    return answer.map((category) => ({
      user_id: userId,
      category_id: 'career',
      question_id: 'career_fields',
      option_id: category,
      value: 'selected',
    }));
  }

  if (key.startsWith(PROFILE_FORM_KEYS.CAREER_PATH_SUBCATEGORY)) {
    if (answer.length === 0) return undefined;

    const categoryId = key.split('_')[2];

    const careerPathCategory = careerPathCategories.find(
      (category) => category.id === parseInt(categoryId),
    );

    return answer.map((field) => ({
      user_id: userId,
      category_id: 'career',
      question_id: 'career_fields',
      suboption_id: 'What subcategory?',
      option_id: careerPathCategory?.name,
      value: field,
    }));
  }

  if (key === PROFILE_FORM_KEYS.ZIP_CODE && answer[0]) {
    return [
      {
        user_id: userId,
        category_id: 'identity',
        question_id: 'zip_code',
        value: answer[0],
      },
    ];
  }

  if (key === PROFILE_FORM_KEYS.LINKEDIN_URL && answer[0]) {
    const linkedInUrl = answer[0]
      .replace('linkedin.com/in/', '')
      .replace('https://', '')
      .replace('www.', '');

    const withUrl = `www.linkedin.com/in/${linkedInUrl}`;

    return [
      {
        user_id: userId,
        category_id: 'linkedIn',
        question_id: 'linkedIn_url',
        value: withUrl,
      },
    ];
  }

  if (key === PROFILE_FORM_KEYS.SCHOOL && answer[0]) {
    if (answer.length === 0) return undefined;

    return answer.map((field) => ({
      user_id: userId,
      category_id: 'education',
      question_id: 'schools',
      value: field,
    }));
  }

  if (!question) return undefined;

  return [];
};

type FormatProfileOptionProps = Readonly<{
  questions: Record<string, OnboardingQuestion>;
}>;

export const formatProfileOption = ({
  questions,
}: FormatProfileOptionProps): BackrsFormField[] | undefined => {
  // first find all of the questions that are of type FORM
  const formQuestions: OnboardingQuestion[] = Object.keys(questions)
    .map((key) => questions[key])
    .filter((question): question is OnboardingQuestion => question?.question.type === 'FORM');

  if (formQuestions.length === 0) return undefined;

  // then we will filter where the post_type is PROFILE_OPTION
  const profileOptionQuestionsFields: BackrsFormField[] = formQuestions
    .map((question) => {
      if (question.question.type !== 'FORM' || !question.question.fields) {
        return undefined;
      }

      const fields: BackrsFormField[] = question?.question.fields;

      if (!fields) return undefined;

      const profileOptionFields = fields.filter((field) => field.post_type === 'PROFILE_OPTION');

      return profileOptionFields;
    })
    .filter((field): field is BackrsFormField[] => field !== undefined)
    .flat();

  return profileOptionQuestionsFields;
};
